import { app } from "@microsoft/teams-js";
import {
  FluentProvider,
  teamsDarkTheme,
  teamsHighContrastTheme,
  teamsLightTheme,
  webLightTheme,
  type Theme,
} from "@fluentui/react-components";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { hasAccount, initAuth } from "../auth/msal";
import { SignIn } from "../components/SignIn";
import { DEMO } from "../config";

export interface HostInfo {
  /** true se l'app gira dentro Teams (o altro host Microsoft 365). */
  inTeams: boolean;
  context?: app.Context;
  /** Gruppo M365 del team corrente (per risalire al sito SharePoint). */
  groupId?: string;
  channelId?: string;
  /** true se aperta come scheda di canale (anche privato o condiviso). */
  inChannel: boolean;
  userPrincipalName?: string;
  locale: string;
}

const HostContext = createContext<HostInfo | undefined>(undefined);

export function useHost(): HostInfo {
  const v = useContext(HostContext);
  if (!v) throw new Error("useHost() fuori da <TeamsProvider>");
  return v;
}

const themes: Record<string, Theme> = {
  default: teamsLightTheme,
  dark: teamsDarkTheme,
  contrast: teamsHighContrastTheme,
};

/** Inizializza Teams JS (con timeout, per poter girare anche nel browser) e l'autenticazione. */
async function bootstrap(): Promise<HostInfo & { needsSignIn: boolean }> {
  const framed = window.parent !== window.self;
  let context: app.Context | undefined;

  if (framed) {
    try {
      await withTimeout(app.initialize(), 5000);
      context = await app.getContext();
    } catch {
      context = undefined;
    }
  }

  const host: HostInfo = {
    inTeams: !!context,
    context,
    // Nei canali privati/condivisi il team "proprietario" è in channel.ownerGroupId
    groupId: context?.channel?.ownerGroupId ?? context?.team?.groupId,
    channelId: context?.channel?.id,
    inChannel: !!context?.channel?.id,
    userPrincipalName: context?.user?.userPrincipalName,
    locale: context?.app.locale ?? navigator.language,
  };

  let needsSignIn = false;
  if (!DEMO) {
    await initAuth(context?.user?.loginHint, !!context);
    needsSignIn = !(await hasAccount());
  }
  if (context) app.notifySuccess();
  return { ...host, needsSignIn };
}

export function TeamsProvider({ children }: { children: ReactNode }) {
  const [host, setHost] = useState<HostInfo>();
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [error, setError] = useState<string>();
  const [theme, setTheme] = useState<Theme>(webLightTheme);

  useEffect(() => {
    bootstrap()
      .then(({ needsSignIn: n, ...h }) => {
        setNeedsSignIn(n);
        setHost(h);
        if (h.context) {
          setTheme(themes[h.context.app.theme] ?? teamsLightTheme);
          app.registerOnThemeChangeHandler((t) => setTheme(themes[t] ?? teamsLightTheme));
        }
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  return (
    <FluentProvider theme={theme} style={{ minHeight: "100vh", background: "transparent" }}>
      {error ? (
        <div style={{ padding: 24 }}>Errore di avvio: {error}</div>
      ) : host && needsSignIn ? (
        <SignIn onDone={() => setNeedsSignIn(false)} />
      ) : host ? (
        <HostContext.Provider value={host}>{children}</HostContext.Provider>
      ) : null}
    </FluentProvider>
  );
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([p, new Promise<T>((_, reject) => setTimeout(() => reject(new Error("timeout")), ms))]);
}
